/**
 * ModelNDetailView — comprehensive per-room detail for Model N / N-f units.
 *
 * Render order (top to bottom):
 *   Project Information → Design Conditions → Room Inputs →
 *   ERV/HRV (Model N only) →
 *   [COOLING PERFORMANCE banner]
 *   Air Mixing → PAC Coil → Spinnaker Coil → System Totals →
 *   Psychrometric Chart → Psychrometric Points table → Water-Side Summary
 *   [HEATING PERFORMANCE banner — only when raw.heating is present]
 *   PAC Coil (heating) → Spinnaker Coil (heating) → System Totals (heating) →
 *   Psychrometric Points (heating) → Water-Side Summary (heating)
 *
 * All data comes from the backend's model-n/calculate response (raw).
 * No physics are re-computed here — every value is sourced directly from
 * the backend response or the summary_table it assembles.
 */

import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { fmtKind, unitLabel } from "../../lib/units";
import { PsychrometricChart } from "../PsychrometricChart";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../ui/tooltip";

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function Section({ title, children, className = "" }) {
  return (
    <div data-pdf-section className={`border border-border rounded overflow-hidden mb-2 break-inside-avoid ${className}`}>
      <div className="bg-muted px-3 py-1.5 text-sm font-semibold border-b border-border text-foreground">
        {title}
      </div>
      <div className="p-2 text-xs">{children}</div>
    </div>
  );
}

/** Render a single numeric data tile (label + value). */
function DataTile({ label, value, sub, accent }) {
  return (
    <div>
      <div className="text-muted-foreground">{label}</div>
      <div className={`font-semibold tabular-nums ${accent ? accent : ""}`}>
        {value ?? "—"}
      </div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

/** Format a temperature with unit label. */
function fmtTemp(val, isSI) {
  if (val == null || val === "" || isNaN(val)) return "—";
  return `${fmtKind(val, "temp", isSI)} ${unitLabel("temp", isSI)}`;
}

/** Format a humidity ratio as grains/lb or g/kg. */
function fmtGrains(grains, isSI) {
  if (grains == null || isNaN(grains)) return "—";
  if (isSI) return `${(grains * 1000 / 7000).toFixed(1)} g/kg`;
  return `${Number(grains).toFixed(1)} gr/lb`;
}

/** Format a % RH. */
function fmtRH(rh) {
  if (rh == null || isNaN(rh)) return "—";
  return `${Number(rh).toFixed(1)}%`;
}

/** Format enthalpy. */
function fmtEnthalpy(h, isSI) {
  if (h == null || isNaN(h)) return "—";
  if (isSI) return `${(h * 2.326).toFixed(1)} kJ/kg`;
  return `${Number(h).toFixed(2)} BTU/lb`;
}

/** One row of the psychrometric points table. */
function PsychRow({ label, point, isSI }) {
  if (!point) return null;
  const db = point.db_f ?? point.dry_bulb_f;
  const wb = point.wb_f ?? point.wet_bulb_f;
  const rh = point.rh ?? point.relative_humidity;
  const grains = point.w_grains ?? point.moisture_grains_lb ?? point.grains_lb;
  const h = point.enthalpy_btu_lb;
  return (
    <TableRow>
      <TableCell className="font-medium">{label}</TableCell>
      <TableCell className="text-right tabular-nums">{fmtTemp(db, isSI)}</TableCell>
      <TableCell className="text-right tabular-nums">{wb != null ? fmtTemp(wb, isSI) : "—"}</TableCell>
      <TableCell className="text-right tabular-nums">{fmtRH(rh)}</TableCell>
      <TableCell className="text-right tabular-nums">{fmtGrains(grains, isSI)}</TableCell>
      <TableCell className="text-right tabular-nums">{fmtEnthalpy(h, isSI)}</TableCell>
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// Big section header banner
// ---------------------------------------------------------------------------

function SectionBanner({ title, color = "navy", pageBreakBefore = false }) {
  const colorClass = color === "warm" ? "bg-amber-900" : "bg-slate-900";
  const breakClass = pageBreakBefore ? "break-before-page" : "";
  return (
    <div className={`${breakClass} ${colorClass} text-white text-sm font-semibold uppercase tracking-wider px-3 py-2 mt-3 mb-0.5 rounded-sm`}>
      {title}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-section: ERV / HRV Energy Recovery
// ---------------------------------------------------------------------------

/** Renders one ERV state-point table (reused for summer and winter columns). */
function ErvTable({ erv, oa_cfm, isSI }) {
  const oa = erv.position_1_outdoor_air || {};
  const sa = erv.position_2_supply_air || {};
  const ra = erv.position_3_return_air || {};
  const ea = erv.position_4_exhaust_air || {};
  const airflowCfm = erv.airflow_cfm ?? oa_cfm;

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
        <DataTile label="Recovery Type" value={erv.type || "HRV"} />
        <DataTile
          label="Sensible Effectiveness"
          value={`${Number(erv.sensible_effectiveness).toFixed(1)}%`}
          accent="text-blue-700 dark:text-blue-400"
        />
        <DataTile
          label="Latent Effectiveness"
          value={`${Number(erv.latent_effectiveness).toFixed(1)}%`}
          accent="text-blue-700 dark:text-blue-400"
        />
        <DataTile
          label="Airflow (OA / EA)"
          value={`${fmtKind(airflowCfm, "flow", isSI)} ${unitLabel("flow", isSI)}`}
        />
      </div>
      <Table className="text-xs [&_th]:h-7 [&_th]:pt-0.5 [&_th]:pb-1.5 [&_td]:pt-0.5 [&_td]:pb-1.5 [&_td]:align-middle">
        <TableHeader>
          <TableRow>
            <TableHead>Position</TableHead>
            <TableHead className="text-right">DB ({unitLabel("temp", isSI)})</TableHead>
            <TableHead className="text-right">WB ({unitLabel("temp", isSI)})</TableHead>
            <TableHead className="text-right">RH (%)</TableHead>
            <TableHead className="text-right">{isSI ? "W (g/kg)" : "W (gr/lb)"}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {[
            { label: "(1) Outdoor Air entering ERV", pt: oa },
            { label: "(2) Supply Air leaving ERV (to mixing box)", pt: sa },
            { label: "(3) Exhaust Air entering ERV (from room)", pt: ra },
            { label: "(4) Exhaust Air leaving ERV (discharged)", pt: ea },
          ].map(({ label, pt }) => (
            <TableRow key={label}>
              <TableCell className="font-medium">{label}</TableCell>
              <TableCell className="text-right tabular-nums">{fmtTemp(pt.db_f, isSI)}</TableCell>
              <TableCell className="text-right tabular-nums">{fmtTemp(pt.wb_f, isSI)}</TableCell>
              <TableCell className="text-right tabular-nums">{fmtRH(pt.rh)}</TableCell>
              <TableCell className="text-right tabular-nums">{fmtGrains(pt.grains_lb, isSI)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
}

function ErvSection({ erv, oa_cfm, isSI, winterErv = null, mode = "both" }) {
  // mode="summer" — render only summer column (erv prop)
  // mode="winter" — render only winter column (winterErv prop)
  // mode="both"   — render side-by-side grid (legacy, kept for safety)

  if (mode === "summer") {
    if (!erv) return null;
    return (
      <Section title="ERV / HRV Energy Recovery — Summer (Cooling)">
        <ErvTable erv={erv} oa_cfm={oa_cfm} isSI={isSI} />
      </Section>
    );
  }

  if (mode === "winter") {
    if (!winterErv) return null;
    return (
      <Section title="ERV / HRV Energy Recovery — Winter (Heating)">
        <ErvTable erv={winterErv} oa_cfm={oa_cfm} isSI={isSI} />
      </Section>
    );
  }

  // mode="both" — original two-column layout
  if (!erv) return null;
  const title = `ERV / HRV Energy Recovery — ${erv.type || "HRV"}`;

  if (!winterErv) {
    return (
      <Section title={title}>
        <ErvTable erv={erv} oa_cfm={oa_cfm} isSI={isSI} />
      </Section>
    );
  }

  return (
    <Section title={title}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="text-xs text-muted-foreground font-semibold uppercase tracking-wide mb-2">Summer (Cooling)</div>
          <ErvTable erv={erv} oa_cfm={oa_cfm} isSI={isSI} />
        </div>
        <div>
          <div className="text-xs text-muted-foreground font-semibold uppercase tracking-wide mb-2">Winter (Heating)</div>
          <ErvTable erv={winterErv} oa_cfm={oa_cfm} isSI={isSI} />
        </div>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Sub-section: Air Mixing (ERV SA + RA → Primary Air to PAC)
// ---------------------------------------------------------------------------

function AirMixingSection({ primaryAir, erv, isNf, isSI }) {
  const mc = primaryAir?.mixed_conditions || {};
  const saCfm = primaryAir?.supply_air_cfm ?? 0;
  const raCfm = primaryAir?.return_air_cfm ?? 0;
  const totalCfm = mc.flow_cfm ?? (saCfm + raCfm);
  const upstreamLabel = isNf ? "Outdoor Air (OA)" : "Post-ERV Supply Air (SA)";

  return (
    <Section title="Air Mixing — Primary Air (PA) at PAC Inlet">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-2">
        <DataTile
          label={upstreamLabel}
          value={`${fmtKind(saCfm, "flow", isSI)} ${unitLabel("flow", isSI)}`}
        />
        <DataTile
          label="Recirculating Room Air (RA)"
          value={`${fmtKind(raCfm, "flow", isSI)} ${unitLabel("flow", isSI)}`}
        />
        <DataTile
          label="Primary Air (PA) total"
          value={`${fmtKind(totalCfm, "flow", isSI)} ${unitLabel("flow", isSI)}`}
          accent="font-bold"
        />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <DataTile label="PA DB" value={fmtTemp(mc.dry_bulb_f, isSI)} />
        <DataTile label="PA WB" value={fmtTemp(mc.wet_bulb_f, isSI)} />
        <DataTile label="PA RH" value={fmtRH(mc.relative_humidity)} />
        <DataTile label="PA Humidity" value={fmtGrains(mc.moisture_grains_lb, isSI)} />
        <DataTile label="PA Enthalpy" value={fmtEnthalpy(mc.enthalpy_btu_lb, isSI)} />
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Sub-section: PAC Coil Performance
// ---------------------------------------------------------------------------

function PacCoilSection({ pacCoil, airSide, waterSide, isSI, sectionTitle }) {
  if (!pacCoil) return null;
  const ae = pacCoil.air_entering || {};
  const al = pacCoil.air_leaving || {};
  const w = pacCoil.water || {};
  const pac = waterSide?.pac || {};

  const ewt = w.entering_temp_f ?? pac.water_entering_f;
  const lwt = w.leaving_temp_f ?? pac.water_leaving_f;
  const dt = w.delta_t_f ?? (lwt != null && ewt != null ? Math.abs(lwt - ewt) : null);
  const gpm = w.flow_gpm ?? pac.water_flow_gpm;
  const pd = pacCoil.water_pd_fth2o ?? pac.water_pd_fth2o;
  const cap = pacCoil.total_capacity_btuh ?? pac.capacity_btuh;
  const sens = pacCoil.sensible_capacity_btuh ?? pac.sensible_capacity_btuh;
  const lat = pacCoil.latent_capacity_btuh ?? pac.latent_capacity_btuh;

  return (
    <Section title={sectionTitle ?? "PAC Coil Performance (Primary Air Coil)"}>
      <SideRow label="Air Side" rows={[
        ["EAT", ae.dry_bulb_f, "temp"],
        ["EA RH", ae.relative_humidity, "rh"],
        ["LAT", al.dry_bulb_f, "temp"],
        ["LA RH", al.relative_humidity, "rh"],
        ["CFM", ae.flow_cfm, "flow"],
        ["Sensible", sens, "capacity"],
        ["Latent", lat, "capacity"],
        ["Total", cap, "capacity"],
      ]} isSI={isSI} />
      <SideRow label="Water Side" rows={[
        ["EWT", ewt, "temp"],
        ["LWT", lwt, "temp"],
        ["ΔT", dt, "tempDelta"],
        ["GPM", gpm, "waterFlow"],
        ["ΔP", pd, "pressure"],
        ["Sensible", sens, "capacity"],
        ["Latent", lat, "capacity"],
        ["Total", cap, "capacity"],
      ]} isSI={isSI} />
    </Section>
  );
}

// Compact horizontal row: side label on the left, label/value mini-cells
// flowing horizontally. Used by PAC + Spinnaker coil sections.
function SideRow({ label, rows, isSI, extra = null }) {
  return (
    <div className="mb-2 last:mb-0">
      <div className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wide mb-1">{label}</div>
      <div className="grid grid-cols-4 md:grid-cols-8 gap-1.5 text-xs">
        {rows.map(([rowLabel, val, kind]) => (
          <div key={rowLabel} className="flex flex-col justify-center bg-muted/30 dark:bg-muted/10 rounded px-2 pt-0.5 pb-1.5">
            <span className="text-[10px] text-muted-foreground leading-tight">{rowLabel}</span>
            <span className="font-semibold tabular-nums leading-tight">
              {kind === "rh" ? fmtRH(val) : `${fmtKind(val, kind, isSI)} ${unitLabel(kind, isSI)}`}
            </span>
          </div>
        ))}
      </div>
      {extra}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-section: Spinnaker Coil Performance
// ---------------------------------------------------------------------------

function SpinnakerCoilSection({ spinCoil, spinInjection, waterSide, isSI, sectionTitle }) {
  if (!spinCoil) return null;
  const ae = spinCoil.air_entering || {};
  const al = spinCoil.air_leaving || {};
  const w = spinCoil.water || {};
  const spin = waterSide?.spinnaker || {};
  const isNonCondensing = spin.is_non_condensing === true;

  const ewt = w.entering_temp_f ?? spin.water_entering_f;
  const lwt = w.leaving_temp_f ?? spin.water_leaving_f;
  const dt = w.delta_t_f ?? (lwt != null && ewt != null ? Math.abs(lwt - ewt) : null);
  const gpmCoil = w.flow_gpm ?? spin.water_flow_gpm;
  // Injection flow: for non-condensing this is spin.water_flow_gpm (injection basis),
  // coil flow is spin.coil_flow_gpm. For condensing, gpmCoil IS the chiller flow.
  const gpmInjection = isNonCondensing ? spin.water_flow_gpm : null;
  const gpmFullCoil = isNonCondensing ? spin.coil_flow_gpm : gpmCoil;
  const pd = spinCoil.water_pd_fth2o ?? spin.water_pd_fth2o;
  const cap = spinCoil.total_capacity_btuh ?? spin.capacity_btuh;
  const sens = spinCoil.sensible_capacity_btuh ?? spin.sensible_capacity_btuh;
  const lat = spinCoil.latent_capacity_btuh ?? spin.latent_capacity_btuh;

  return (
    <Section title={sectionTitle ?? "Spinnaker Coil Performance (Secondary / Induced Air Coil)"}>
      <div className="mb-2 flex gap-2 items-center">
        <span className={`text-[10px] font-semibold uppercase tracking-wide leading-none px-2 pt-0.5 pb-1.5 rounded ${isNonCondensing
          ? "bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300"
          : "bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300"}`}>
          {isNonCondensing ? "Non-Condensing Mode" : "Condensing Mode (Max Rating)"}
        </span>
        {isNonCondensing && (
          <span className="text-[10px] text-muted-foreground">
            Mixed water via injection valve — chiller sees injection flow only
          </span>
        )}
      </div>

      <SideRow label="Air Side" rows={[
        ["EAT", ae.dry_bulb_f, "temp"],
        ["EA RH", ae.relative_humidity, "rh"],
        ["LAT", al.dry_bulb_f, "temp"],
        ["LA RH", al.relative_humidity, "rh"],
        ["CFM", ae.flow_cfm, "flow"],
        ["Sensible", sens, "capacity"],
        ["Latent", lat, "capacity"],
        ["Total", cap, "capacity"],
      ]} isSI={isSI} />
      <SideRow label="Water Side" rows={[
        ["EWT", ewt, "temp"],
        ["LWT", lwt, "temp"],
        ["ΔT", dt, "tempDelta"],
        ["GPM", gpmCoil, "waterFlow"],
        ["ΔP", pd, "pressure"],
        ["Sensible", sens, "capacity"],
        ["Latent", lat, "capacity"],
        ["Total", cap, "capacity"],
      ]} isSI={isSI} extra={isNonCondensing ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5 text-xs mt-1.5">
          <div className="flex flex-col rounded px-2 py-1 bg-amber-100/50 dark:bg-amber-950/30 border border-amber-300/40">
            <span className="text-[10px] text-amber-700 dark:text-amber-400 leading-tight">Injection Flow (chiller)</span>
            <span className="font-semibold tabular-nums text-amber-700 dark:text-amber-400 leading-tight">{fmtKind(gpmInjection, "waterFlow", isSI)} {unitLabel("waterFlow", isSI)}</span>
          </div>
          <div className="flex flex-col bg-muted/30 dark:bg-muted/10 rounded px-2 py-1">
            <span className="text-[10px] text-muted-foreground leading-tight">Coil Flow (recirculating)</span>
            <span className="font-semibold tabular-nums leading-tight">{fmtKind(gpmFullCoil, "waterFlow", isSI)} {unitLabel("waterFlow", isSI)}</span>
          </div>
        </div>
      ) : null} />
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Sub-section: System Totals
// ---------------------------------------------------------------------------

function SystemTotalsSection({ airSide, waterSide, isSI, sectionTitle }) {
  const ai = airSide || {};
  const ws = waterSide || {};

  const totalChillerFlow = ws.total_water_flow_gpm;
  const totalChillerCap = ws.total_chiller_capacity_btuh;
  const totalAirCap = ai.total_capacity_btuh;
  const oaCondLoad = totalChillerCap != null && totalAirCap != null
    ? totalChillerCap - totalAirCap
    : null;

  return (
    <Section title={sectionTitle ?? "System Totals — Chiller Plant Sizing"}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
        <DataTile
          label="Total Supply Air to Room"
          value={`${fmtKind(ai.total_airflow_cfm, "flow", isSI)} ${unitLabel("flow", isSI)}`}
          sub={`PAC ${fmtKind(ai.pac_airflow_cfm, "flow", isSI)} + Spinnaker ${fmtKind(ai.spinnaker_airflow_cfm, "flow", isSI)} ${unitLabel("flow", isSI)}`}
        />
        <DataTile
          label="Total Chiller Flow"
          value={`${fmtKind(totalChillerFlow, "waterFlow", isSI)} ${unitLabel("waterFlow", isSI)}`}
          accent="font-bold text-blue-700 dark:text-blue-400"
          sub="PAC + Spinnaker chiller-side"
        />
        <DataTile
          label="Total Chiller Capacity"
          value={`${fmtKind(totalChillerCap, "capacity", isSI)} ${unitLabel("capacity", isSI)}`}
          sub="Water-side (incl. OA conditioning)"
        />
        <DataTile
          label="Delivered to Room (Air-Side)"
          value={`${fmtKind(totalAirCap, "capacity", isSI)} ${unitLabel("capacity", isSI)}`}
        />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <DataTile
          label="Room Sensible (air-side)"
          value={`${fmtKind(ai.sensible_capacity_btuh, "capacity", isSI)} ${unitLabel("capacity", isSI)}`}
        />
        <DataTile
          label="Room Latent (air-side)"
          value={`${fmtKind(ai.latent_capacity_btuh, "capacity", isSI)} ${unitLabel("capacity", isSI)}`}
        />
        {oaCondLoad != null && oaCondLoad > 0 && (
          <DataTile
            label="OA Conditioning Load"
            value={`${fmtKind(oaCondLoad, "capacity", isSI)} ${unitLabel("capacity", isSI)}`}
            sub="Water-side − air-side (outdoor air pre-cooling)"
          />
        )}
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Sub-section: Water-Side Summary table (bottom executive summary)
// ---------------------------------------------------------------------------

function WaterSideSummary({ waterSide, isSI, sectionTitle }) {
  const ws = waterSide || {};
  return (
    <Section title={sectionTitle ?? "Water-Side Summary (Executive — Plant Sizing)"}>
      <Table className="text-xs [&_th]:h-7 [&_th]:pt-0.5 [&_th]:pb-1.5 [&_td]:pt-0.5 [&_td]:pb-1.5 [&_td]:align-middle">
        <TableHeader>
          <TableRow>
            <TableHead>Coil</TableHead>
            <TableHead className="text-right">Flow ({unitLabel("waterFlow", isSI)})</TableHead>
            <TableHead className="text-right">EWT ({unitLabel("temp", isSI)})</TableHead>
            <TableHead className="text-right">LWT ({unitLabel("temp", isSI)})</TableHead>
            <TableHead className="text-right">PD ({unitLabel("pressure", isSI)})</TableHead>
            <TableHead className="text-right">Capacity ({unitLabel("capacity", isSI)})</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {["pac", "spinnaker"].map((k) => {
            const c = ws[k] || {};
            const injectionFlow = c.water_flow_gpm;
            const coilFlow = c.coil_flow_gpm;
            // Show dual flow only in non-condensing (mixing valve) mode.
            // In condensing mode coil_flow_gpm is absent → fall back to single.
            const hasDualFlow =
              k === "spinnaker" &&
              coilFlow != null &&
              !isNaN(coilFlow) &&
              coilFlow !== injectionFlow;

            const flowCell = hasDualFlow ? (
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="font-mono tabular-nums cursor-default underline decoration-dotted underline-offset-2">
                      {injectionFlow != null && !isNaN(injectionFlow)
                        ? fmtKind(injectionFlow, "waterFlow", isSI)
                        : "—"}
                      {" / "}
                      {coilFlow != null && !isNaN(coilFlow)
                        ? fmtKind(coilFlow, "waterFlow", isSI)
                        : "—"}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs leading-snug">
                    <p>
                      <strong>Injection (chiller-side):</strong>{" "}
                      {fmtKind(injectionFlow, "waterFlow", isSI)} {unitLabel("waterFlow", isSI)} — supplied by chiller plant.
                    </p>
                    <p className="mt-0.5">
                      <strong>Coil (recirculating):</strong>{" "}
                      {fmtKind(coilFlow, "waterFlow", isSI)} {unitLabel("waterFlow", isSI)} — internal mixing-valve loop.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              fmtKind(injectionFlow, "waterFlow", isSI)
            );

            return (
              <TableRow key={k}>
                <TableCell className="font-medium capitalize">{k}</TableCell>
                <TableCell className="text-right">{flowCell}</TableCell>
                <TableCell className="text-right">{fmtKind(c.water_entering_f, "temp", isSI)}</TableCell>
                <TableCell className="text-right">{fmtKind(c.water_leaving_f, "temp", isSI)}</TableCell>
                <TableCell className="text-right">{fmtKind(c.water_pd_fth2o, "pressure", isSI)}</TableCell>
                <TableCell className="text-right">{fmtKind(c.capacity_btuh, "capacity", isSI)}</TableCell>
              </TableRow>
            );
          })}
          <TableRow className="bg-muted font-semibold">
            <TableCell>Total (chiller plant)</TableCell>
            <TableCell className="text-right">{fmtKind(ws.total_water_flow_gpm, "waterFlow", isSI)}</TableCell>
            <TableCell colSpan={2}></TableCell>
            <TableCell className="text-right">{fmtKind(ws.total_water_pd_fth2o, "pressure", isSI)}</TableCell>
            <TableCell className="text-right">{fmtKind(ws.total_chiller_capacity_btuh, "capacity", isSI)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Sub-section: Psychrometric Points table
// ---------------------------------------------------------------------------

const PSYCH_POINT_LABELS = {
  OA:       "OA — Outdoor Air",
  EA:       "EA — Exhaust Air (room)",
  ERV_SA:   "SA (post-ERV) — Supply Air after recovery",
  RA:       "RA — Room / Return Air",
  PA:       "PA — Primary Air (mixed, PAC inlet)",
  PAC_OUT:  "LAT PAC — Leaving Air Temperature (PAC)",
  SPIN_OUT: "LAT Spinnaker — Leaving Air Temperature (Spinnaker)",
  SA:       "SA (final) — Mixed Supply Air to Space",
};

function PsychrometricPointsTable({ points, isSI, sectionTitle }) {
  if (!points) return null;

  // Ordered list: OA → EA → ERV_SA → RA → PA → PAC_OUT → SPIN_OUT → SA
  const ORDER = ["OA", "EA", "ERV_SA", "RA", "PA", "PAC_OUT", "SPIN_OUT", "SA"];

  return (
    <Section title={sectionTitle ?? "Psychrometric State Points (Tabular)"}>
      <Table className="text-xs [&_th]:h-7 [&_th]:pt-0.5 [&_th]:pb-1.5 [&_td]:pt-0.5 [&_td]:pb-1.5 [&_td]:align-middle">
        <TableHeader>
          <TableRow>
            <TableHead>State Point</TableHead>
            <TableHead className="text-right">DB ({unitLabel("temp", isSI)})</TableHead>
            <TableHead className="text-right">WB ({unitLabel("temp", isSI)})</TableHead>
            <TableHead className="text-right">RH (%)</TableHead>
            <TableHead className="text-right">{isSI ? "W (g/kg)" : "W (gr/lb)"}</TableHead>
            <TableHead className="text-right">{isSI ? "h (kJ/kg)" : "h (BTU/lb)"}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {ORDER.map((key) => {
            if (!points[key]) return null;
            return (
              <PsychRow
                key={key}
                label={PSYCH_POINT_LABELS[key] || key}
                point={points[key]}
                isSI={isSI}
              />
            );
          })}
        </TableBody>
      </Table>
      <p className="text-[10px] text-muted-foreground mt-1">
        All state points are from the backend calculation. h (enthalpy) shown only when available in backend response.
      </p>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Main export: ModelNDetailView
// ---------------------------------------------------------------------------

/**
 * Renders the full comprehensive detail view for a Model N or N-f room result.
 *
 * Props:
 *   raw         — the backend API response object (raw.summary_table, raw.pac_coil, etc.)
 *   isSI        — boolean; true = SI display units
 *   showChart   — boolean; whether to render the psychrometric chart
 *   isNf        — boolean; true when this is Model N-f (no ERV)
 */
export default function ModelNDetailView({ raw, isSI, showChart = true, isNf = false }) {
  if (!raw) return null;

  const ai = raw.summary_table?.air_side || {};
  const ws = raw.summary_table?.water_side || {};
  const heating = raw.heating || null;
  const erv = raw.summary_table?.erv_hrv || null;
  const primaryAir = raw.primary_air || null;
  const pacCoil = raw.pac_coil || null;
  const spinCoil = raw.spinnaker_coil || null;
  const spinInjection = raw.spinnaker_injection || null;
  const psych = raw.psych_chart || null;

  return (
    <>
      <SectionBanner title="Cooling Performance" pageBreakBefore />

      {/* 4.0. ERV / HRV — Summer (Cooling) — skipped for Model N-f */}
      {!isNf && erv && (
        <ErvSection
          erv={erv}
          oa_cfm={primaryAir?.supply_air_cfm}
          isSI={isSI}
          mode="summer"
        />
      )}

      {/* 4a. Air Mixing */}
      {primaryAir && (
        <AirMixingSection primaryAir={primaryAir} erv={erv} isNf={isNf} isSI={isSI} />
      )}

      {/* 4b. PAC Coil Performance (Primary Air Coil) */}
      <PacCoilSection pacCoil={pacCoil} airSide={ai} waterSide={ws} isSI={isSI} />

      {/* 4c. Spinnaker Coil Performance (Secondary) */}
      <SpinnakerCoilSection
        spinCoil={spinCoil}
        spinInjection={spinInjection}
        waterSide={ws}
        isSI={isSI}
      />

      {/* 4d. System Totals — Chiller Plant Sizing */}
      <SystemTotalsSection airSide={ai} waterSide={ws} isSI={isSI} />

      {/* 4e. Psychrometric Chart */}
      {showChart && psych?.points && (
        <Section title="Psychrometric Chart">
          <PsychrometricChart data={psych} isSI={isSI} />
        </Section>
      )}

      {/* 4f. Psychrometric State Points (Tabular) */}
      {psych?.points && (
        <PsychrometricPointsTable points={psych.points} isSI={isSI} />
      )}

      {/* 4g. Water-Side Summary */}
      <WaterSideSummary waterSide={ws} isSI={isSI} />

      {/* Heating block — only when backend returned raw.heating */}
      {heating && (
        <>
          <SectionBanner title="Heating Performance" color="warm" pageBreakBefore />

          {/* 5.0. ERV / HRV — Winter (Heating) — skipped for Model N-f */}
          {!isNf && heating.erv_hrv && (
            <ErvSection
              erv={null}
              winterErv={heating.erv_hrv}
              oa_cfm={primaryAir?.supply_air_cfm}
              isSI={isSI}
              mode="winter"
            />
          )}

          {/* 5a. PAC Coil Performance (Heating Mode) */}
          <PacCoilSection
            pacCoil={heating.pac_coil || null}
            airSide={heating.summary_table?.air_side || {}}
            waterSide={heating.summary_table?.water_side || {}}
            isSI={isSI}
            sectionTitle="PAC Coil Performance (Heating Mode)"
          />

          {/* 5b. Spinnaker Coil Performance (Heating Mode) */}
          <SpinnakerCoilSection
            spinCoil={heating.spinnaker_coil || null}
            spinInjection={null}
            waterSide={heating.summary_table?.water_side || {}}
            isSI={isSI}
            sectionTitle="Spinnaker Coil Performance (Heating Mode)"
          />

          {/* 5c. System Totals — Heating Plant Sizing */}
          <SystemTotalsSection
            airSide={heating.summary_table?.air_side || {}}
            waterSide={heating.summary_table?.water_side || {}}
            isSI={isSI}
            sectionTitle="System Totals — Heating Plant Sizing"
          />

          {/* 5d. Psychrometric Chart — Heating */}
          {showChart && raw.heating?.psych_chart?.points && (
            <Section title="Psychrometric Chart — Heating">
              <PsychrometricChart data={raw.heating.psych_chart} isSI={isSI} />
            </Section>
          )}

          {/* 5e. Psychrometric State Points (Tabular) — Heating */}
          {raw.heating?.psych_chart?.points && (
            <PsychrometricPointsTable
              points={raw.heating.psych_chart.points}
              isSI={isSI}
              sectionTitle="Psychrometric State Points (Tabular) — Heating"
            />
          )}

          {/* 5f. Water-Side Summary — Heating */}
          {heating.summary_table?.water_side && (
            <WaterSideSummary
              waterSide={heating.summary_table.water_side}
              isSI={isSI}
              sectionTitle="Water-Side Summary — Heating"
            />
          )}
        </>
      )}
    </>
  );
}
